import { Box, Button, Dialog, IconButton, Theme, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import { useCallback, useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  SkuContentRule,
  Skus,
  AdminListRequestOfBaseFilter,
  ISkuContentRuleOrganization,
  ListResponseOfSkuContentRuleOrganization,
  SkuContentRuleOrganizationRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import useBoolean from "hooks/useBoolean";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { useTheme } from "@mui/material/styles";
import SkuRuleDetailOrganizationForm from "./SkuRuleDetailOrganizationForm";

type SkuRuleDetailOrganizationsListProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  dialogTitleKey: string;
};

type SkuRuleDetailOrganizationsFormValues = {
  organizations: ISkuContentRuleOrganization[];
};

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const { Form } = withTypes<SkuRuleDetailOrganizationsFormValues>();

const SkuRuleDetailOrganizationsList = ({
  sku,
  skuContentRule,
  readonly,
  dialogTitleKey,
  ...props
}: SkuRuleDetailOrganizationsListProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const styles = useStyles(useTheme());
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<ISkuContentRuleOrganization | null>(
    null
  );
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    skuRuleOrganizationToEdit,
    setSkuRuleOrganizationToEdit,
  ] = useState<ISkuContentRuleOrganization | null>(null);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("organization.name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await skuService.deleteSkuContentRuleOrganization(
          sku.id!,
          skuContentRule.id,
          id
        );
        queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuRuleOrganizationFilterDeleted" })
          )
        );
      } catch (err) {
        console.log(err);
      }
    }
  }, [deleting?.id, skuService, sku.id, skuContentRule.id, queryClient, t]);

  const response = useQuery<ListResponseOfSkuContentRuleOrganization>(
    [
      "skuRuleDetails",
      sku.id,
      skuContentRule.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      skuService.getSkuContentRuleOrganizations(
        sku.id!,
        skuContentRule.id,
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      )
  );
  interface SkuContentRuleOrganizationWithId
    extends ISkuContentRuleOrganization {
    id: string;
  }

  const skuContentRuleOrganizations = (response?.data?.data ?? []) as SkuContentRuleOrganizationWithId[];

  const init = useMemo(
    () => ({
      organizations: skuContentRuleOrganizations,
    }),
    [skuContentRuleOrganizations]
  );

  if (response.isLoading) return <Loader />;
  if (response.isError) {
    console.log(response.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onSubmit = async (values: SkuRuleDetailOrganizationsFormValues) => {
    try {
      await skuService.saveSkuContentRuleOrganization(
        sku.id!,
        skuContentRule.id,
        SkuContentRuleOrganizationRequest.fromJS(values)
      );

      queryClient.invalidateQueries([
        "skuRuleDetails",
        sku.id,
        skuContentRule.id,
      ]);

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "ruleUpdatedMessage" }))
      );
    } catch (err) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSkuRuleEditClose = () => {
    createOpenActions.setFalse();
    setSkuRuleOrganizationToEdit(null);
  };

  const onSkuRuleCreated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const onSkuRuleUpdated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const DeleteButton = ({ value }: { value: ISkuContentRuleOrganization }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => setDeleting(value)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: ISkuContentRuleOrganization }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => {
            setSkuRuleOrganizationToEdit(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "ownerOrganization" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitError }) => {
        return (
          <form onSubmit={handleSubmit}>
            {!readonly && (
              <Box textAlign="right">
                <Button
                  onClick={createOpenActions.setTrue}
                  startIcon={<AddIcon />}
                >
                  <T id="addFilterButton" />
                </Button>
              </Box>
            )}

            <LazyTable
              {...{
                rows: skuContentRuleOrganizations,
                loading: response.isFetching,
                dense: true,
                cells,
                rowCount: response?.data?.totalRecords ?? 0,
                page,
                setPage,
                order,
                setOrder,
                orderBy,
                setOrderBy,
                rowsPerPage,
                setRowsPerPage,
              }}
            />
            {submitError && (
              <Alert sx={styles.feedback} severity="error">
                {submitError}
              </Alert>
            )}

            <DeleteEntityDialog
              dialogTitleKey="skuRuleOrganizationFilterDeleteTitle"
              contentKey="skuRuleFilterDeleteQuestion"
              open={!!deleting}
              handleDelete={handleDelete}
              onCancel={() => setDeleting(null)}
            />
            <Dialog
              open={createOpen}
              disableEnforceFocus
              maxWidth="sm"
              fullWidth
            >
              <SkuRuleDetailOrganizationForm
                onClose={onSkuRuleEditClose}
                onCreate={onSkuRuleCreated}
                onUpdate={onSkuRuleUpdated}
                skuId={skuContentRule.skuId}
                skuRuleId={skuContentRule.id}
                skuContentRuleOrganization={skuRuleOrganizationToEdit}
                dialogTitleKey={dialogTitleKey}
                {...props}
              />
            </Dialog>
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        );
      }}
    />
  );
};

export default SkuRuleDetailOrganizationsList;
